import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import { 
  BookOpen,
  Users,
  GraduationCap,
  Handshake,
  Award,
  Lightbulb,
  Rocket,
  Heart,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Target,
  Compass,
  Shield
} from 'lucide-react';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 py-20 px-4">
        {/* Decorative blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-violet-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-fuchsia-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-medium mb-6" data-testid="hero-badge">
            <Sparkles size={16} />
            Ingénierie pédagogique & Formation professionnelle
          </div>
          
          <h2 className="text-5xl md:text-7xl font-bold text-gray-800 mb-6 leading-tight">
            L'écosystème complet
            <br />
            <span className="gradient-text">de vos formations</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            Que vous souhaitiez <strong className="text-violet-600">vous former</strong> ou <strong className="text-purple-600">partager votre expertise</strong>, 
            Insitumed vous accompagne dans la construction et l'organisation de votre projet pédagogique.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/catalogue" className="btn-violet text-lg px-8 py-4 flex items-center gap-2" data-testid="hero-cta-formations">
              <BookOpen size={20} />
              Voir les formations
              <ArrowRight size={20} />
            </Link>
            <Link to="/partenaire" className="text-lg px-8 py-4 flex items-center gap-2 border-2 border-violet-500 text-violet-600 rounded-lg hover:bg-violet-50 transition-colors font-semibold" data-testid="hero-cta-partenaire">
              <Handshake size={20} />
              Devenir formateur
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
              <Target size={16} />
              Notre mission
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Qu'est-ce que <span className="gradient-text">Insitumed</span> ?
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Insitumed est un centre de formation professionnelle qui allie <strong>expertise pédagogique</strong> et 
              <strong> accompagnement personnalisé</strong>. Nous croyons qu'apprendre et transmettre sont des voies 
              parallèles d'un même chemin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-violet-50 to-white border border-violet-100 hover:shadow-lg transition-all">
              <div className="inline-flex p-4 bg-violet-100 rounded-2xl mb-4">
                <Lightbulb className="text-violet-600" size={32} />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-3">Ingénierie pédagogique</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Nous concevons des parcours de formation sur-mesure, alignés sur vos objectifs et adaptés à votre public.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-white border border-purple-100 hover:shadow-lg transition-all">
              <div className="inline-flex p-4 bg-purple-100 rounded-2xl mb-4">
                <Compass className="text-purple-600" size={32} />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-3">Accompagnement</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                De la conception à la mise en œuvre, nous vous guidons à chaque étape de votre projet de formation.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-fuchsia-50 to-white border border-fuchsia-100 hover:shadow-lg transition-all">
              <div className="inline-flex p-4 bg-fuchsia-100 rounded-2xl mb-4">
                <Heart className="text-fuchsia-600" size={32} />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-3">Partenariat</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Nous travaillons main dans la main avec formateurs et partenaires pour créer des expériences enrichissantes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Paths Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-white to-violet-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium mb-4">
              <Rocket size={16} />
              Deux voies, un même objectif
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Choisissez <span className="gradient-text">votre parcours</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Insitumed s'adresse à celles et ceux qui veulent grandir, apprendre ou transmettre.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Path 1: Learners */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-violet-100 hover:shadow-2xl transition-all">
              <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-8">
                <div className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-2xl mb-4">
                  <GraduationCap className="text-white" size={40} />
                </div>
                <h4 className="text-3xl font-bold text-white mb-2">Je veux me former</h4>
                <p className="text-violet-100 text-lg">
                  Développez vos compétences avec nos formations professionnelles
                </p>
              </div>
              
              <div className="p-8">
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Consultez notre <strong>catalogue de formations</strong> professionnelles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Inscrivez-vous en ligne <strong>simplement et rapidement</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Recevez vos <strong>convocations et attestations de formation</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Accédez à un <strong>espace personnel</strong> pour suivre votre parcours</span>
                  </li>
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/catalogue" className="btn-violet flex-1 flex items-center justify-center gap-2" data-testid="path-learner-cta">
                    <BookOpen size={18} />
                    Voir le catalogue
                  </Link>
                  <Link to="/mon-espace" className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border-2 border-violet-500 text-violet-600 rounded-lg hover:bg-violet-50 transition-colors font-semibold" data-testid="path-learner-space">
                    <Users size={18} />
                    Mon espace
                  </Link>
                </div>
              </div>
            </div>

            {/* Path 2: Trainers/Partners */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-fuchsia-100 hover:shadow-2xl transition-all">
              <div className="bg-gradient-to-r from-fuchsia-500 to-pink-600 p-8">
                <div className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-2xl mb-4">
                  <Handshake className="text-white" size={40} />
                </div>
                <h4 className="text-3xl font-bold text-white mb-2">Je veux transmettre</h4>
                <p className="text-fuchsia-100 text-lg">
                  Partagez votre expertise avec l'accompagnement Insitumed
                </p>
              </div>
              
              <div className="p-8">
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Vous démarrez comme formateur ? <strong>Nous vous accompagnons</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700"><strong>Ingénierie pédagogique</strong> : nous structurons votre formation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700"><strong>Logistique complète</strong> : inscriptions, paiements, documents</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">Concentrez-vous sur ce que vous savez faire : <strong>enseigner</strong></span>
                  </li>
                </ul>

                <Link to="/partenaire" className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity font-semibold" data-testid="path-partner-cta">
                  <Handshake size={18} />
                  Proposer ma formation
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
              <Sparkles size={16} />
              Notre engagement
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Pourquoi choisir <span className="gradient-text">Insitumed</span> ?
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-gradient-to-br from-violet-50 to-white border border-violet-100">
              <div className="inline-flex p-3 bg-violet-100 rounded-xl mb-4">
                <Award className="text-violet-600" size={24} />
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">Formations reconnues</h4>
              <p className="text-sm text-gray-600">
                Attestations de présence et de fin de formation officielles remises à l'issue de chaque parcours.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-white border border-purple-100">
              <div className="inline-flex p-3 bg-purple-100 rounded-xl mb-4">
                <Users className="text-purple-600" size={24} />
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">Petits groupes</h4>
              <p className="text-sm text-gray-600">
                Places limitées pour un accompagnement personnalisé et de qualité.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-fuchsia-50 to-white border border-fuchsia-100">
              <div className="inline-flex p-3 bg-fuchsia-100 rounded-xl mb-4">
                <Shield className="text-fuchsia-600" size={24} />
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">Paiement sécurisé</h4>
              <p className="text-sm text-gray-600">
                Virement bancaire sécurisé, sans frais cachés ni surprises.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-pink-50 to-white border border-pink-100">
              <div className="inline-flex p-3 bg-pink-100 rounded-xl mb-4">
                <Heart className="text-pink-600" size={24} />
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">À l'écoute</h4>
              <p className="text-sm text-gray-600">
                Une équipe humaine et réactive pour vous accompagner à chaque étape.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-4xl mx-auto text-center text-white">
          <h3 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt(e) à démarrer votre projet ?
          </h3>
          <p className="text-xl text-violet-100 mb-10 leading-relaxed">
            Rejoignez Insitumed dès aujourd'hui, que ce soit pour apprendre ou pour transmettre.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/catalogue" className="bg-white text-violet-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-violet-50 transition-colors flex items-center justify-center gap-2" data-testid="footer-cta-formations">
              <BookOpen size={20} />
              Voir les formations
            </Link>
            <Link to="/partenaire" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-2" data-testid="footer-cta-partenaire">
              <Handshake size={20} />
              Devenir formateur
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold gradient-text mb-3">Insitumed</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Centre de Formation Professionnelle spécialisé en ingénierie pédagogique. 
                Nous accompagnons apprenants et formateurs dans leurs projets.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Se former</h4>
              <ul className="space-y-2">
                <li><Link to="/catalogue" className="text-gray-400 hover:text-white text-sm transition-colors">Catalogue</Link></li>
                <li><Link to="/mon-espace" className="text-gray-400 hover:text-white text-sm transition-colors">Espace apprenant</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Transmettre</h4>
              <ul className="space-y-2">
                <li><Link to="/partenaire" className="text-gray-400 hover:text-white text-sm transition-colors">Devenir formateur</Link></li>
                <li><a href="mailto:insitumed@myyahoo.com" className="text-gray-400 hover:text-white text-sm transition-colors">Nous contacter</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-500 mb-4">
              <div>
                <span className="text-gray-400 font-semibold">SIRET</span>
                <br />789 332 053 00055
              </div>
              <div>
                <span className="text-gray-400 font-semibold">Code APE</span>
                <br />8559B · Profession libérale réglementée
              </div>
              <div>
                <span className="text-gray-400 font-semibold">Date de création</span>
                <br />1<sup>er</sup> juillet 2025
              </div>
            </div>
            <p className="text-gray-400 text-sm text-center">
              © 2025 Insitumed - Tous droits réservés · <a href="mailto:insitumed@myyahoo.com" className="hover:text-white transition-colors">insitumed@myyahoo.com</a>
              {' · '}
              <Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
